import { useEffect, useState } from 'react';
import { CheckCircle, XCircle, Loader2, ShieldCheck } from 'lucide-react';

/**
 * Página pública (sin auth) de verificación VeriFactu.
 * URL: /v/{tenantId}?n={invoice_number}&h={short_hash}&k={kind}
 *
 * El QR de las facturas apunta aquí. Cualquiera con el QR puede comprobar
 * que la factura existe en MedCover Pro y el hash coincide.
 */
export default function VerifactuVerifyPage() {
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    const path = window.location.pathname; // /v/{tenantId}
    const tenantId = path.replace(/^\/v\//, '').split('/')[0];
    const params = new URLSearchParams(window.location.search);
    const n = params.get('n');
    const h = params.get('h');
    const k = params.get('k') || 'client';

    if (!tenantId || !n || !h) {
      setState({ loading: false, valid: false, reason: 'missing_params' });
      return;
    }

    fetch(`/api/verifactu/verify?tenant=${encodeURIComponent(tenantId)}&n=${encodeURIComponent(n)}&h=${encodeURIComponent(h)}&k=${encodeURIComponent(k)}`)
      .then(r => r.json())
      .then(d => setState({ loading: false, ...d }))
      .catch(() => setState({ loading: false, valid: false, reason: 'network_error' }));
  }, []);

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(135deg, #f1f5f9, #e2e8f0)',
      padding: 20,
    }}>
      <div style={{
        background: '#fff', borderRadius: 16, padding: 32,
        maxWidth: 480, width: '100%', boxShadow: '0 12px 32px rgba(0,0,0,0.08)',
        border: '1px solid #e2e8f0',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
          <div style={{ width: 44, height: 44, borderRadius: 10, background: 'linear-gradient(135deg, #059669, #2563EB)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ShieldCheck size={22} color="#fff" />
          </div>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#1e293b' }}>VeriFactu</div>
            <div style={{ fontSize: 12, color: '#64748b' }}>Sistema de verificación de facturas</div>
          </div>
        </div>

        {state.loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: 32 }}>
            <Loader2 size={32} className="spinner" style={{ color: '#2563EB' }} />
            <div style={{ fontSize: 14, color: '#64748b' }}>Verificando factura…</div>
          </div>
        ) : state.valid ? (
          <div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '16px 0 24px', borderBottom: '1px solid #e2e8f0', marginBottom: 16 }}>
              <CheckCircle size={56} color="#10B981" />
              <div style={{ fontSize: 18, fontWeight: 800, color: '#10B981' }}>Factura auténtica</div>
              <div style={{ fontSize: 12, color: '#64748b', textAlign: 'center', maxWidth: 360 }}>
                La huella criptográfica coincide con el registro en MedCover Pro.
                Esta factura no ha sido alterada desde su emisión.
              </div>
            </div>
            <div style={{ display: 'grid', gap: 8, fontSize: 13 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#64748b' }}>Número</span>
                <strong style={{ fontFamily: 'monospace' }}>{state.invoice_number}</strong>
              </div>
              {state.issue_date && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#64748b' }}>Fecha emisión</span>
                  <strong>{new Date(state.issue_date).toLocaleDateString('es-ES')}</strong>
                </div>
              )}
              {state.total != null && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#64748b' }}>Total</span>
                  <strong style={{ color: '#059669' }}>€{Number(state.total).toFixed(2)}</strong>
                </div>
              )}
              {state.signed_at && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#64748b' }}>Firmada</span>
                  <span style={{ fontSize: 12 }}>{new Date(state.signed_at).toLocaleString('es-ES')}</span>
                </div>
              )}
              {state.full_hash && (
                <div style={{ marginTop: 8, padding: '10px 12px', background: '#f1f5f9', borderRadius: 8 }}>
                  <div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase', marginBottom: 4 }}>Huella SHA-256</div>
                  <div style={{ fontSize: 10, fontFamily: 'monospace', color: '#1e293b', wordBreak: 'break-all' }}>{state.full_hash}</div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '16px 0' }}>
            <XCircle size={56} color="#EF4444" />
            <div style={{ fontSize: 18, fontWeight: 800, color: '#EF4444' }}>No verificada</div>
            <div style={{ fontSize: 13, color: '#64748b', textAlign: 'center', maxWidth: 360 }}>
              {state.reason === 'not_found'
                ? 'No encontramos esta factura en el sistema. Puede haber sido cancelada o el código QR es incorrecto.'
                : state.reason === 'missing_params'
                  ? 'El enlace de verificación está incompleto. Asegúrate de escanear el QR completo.'
                  : 'No se pudo verificar la factura. Intenta de nuevo.'}
            </div>
          </div>
        )}

        <div style={{ marginTop: 20, padding: '10px 12px', background: '#f8fafc', borderRadius: 8, fontSize: 11, color: '#64748b', textAlign: 'center' }}>
          Esta verificación cumple con el RD 1007/2023 (Reglamento VeriFactu).
        </div>
      </div>
    </div>
  );
}
